import React from "react";
import { thumbUrl } from "@/lib/utils-app";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScissorsIcon as Scissors } from "@phosphor-icons/react";

const TONE = {
  "Excellent": "bg-emerald-100 text-emerald-800 border-emerald-300",
  "Good": "bg-sky-100 text-sky-900 border-sky-300",
  "Needs attention": "bg-amber-100 text-amber-800 border-amber-300",
};

function Grade({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between">
      <span className="text-[13px] font-semibold text-ink">{label}</span>
      <span className={`text-[11px] font-bold rounded-full px-2.5 py-0.5 border ${TONE[value] || TONE.Good}`}>{value}</span>
    </div>
  );
}

/** Customer-facing groom report card — photos, grades, notes. */
export default function GroomReportView({ open, booking, onClose }) {
  if (!booking?.groom_report) return null;
  const r = booking.groom_report;
  const before = r.before_url ? (thumbUrl(r.before_url) || r.before_url) : null;
  const after = r.after_url ? (thumbUrl(r.after_url) || r.after_url) : null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="groom-report-view">
        <DialogHeader>
          <DialogTitle className="font-spectral flex items-center gap-2">
            <Scissors size={18} weight="duotone" className="text-gold" /> Groom report card
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            {booking.provider_business ? `From ${booking.provider_business} · ` : ""}
            {r.sent_at ? new Date(r.sent_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : ""}
          </DialogDescription>
        </DialogHeader>
        {(before || after) && (
          <div className="flex gap-3">
            {before && (
              <figure className="flex-1">
                <img src={before} alt="Before" className="w-full h-36 object-cover rounded-lg border border-[#EAEAEA]" data-testid="groom-before-photo" />
                <figcaption className="text-[11px] text-[#6B6B6B] text-center mt-1 uppercase tracking-wider font-bold">Before</figcaption>
              </figure>
            )}
            {after && (
              <figure className="flex-1">
                <img src={after} alt="After" className="w-full h-36 object-cover rounded-lg border-2 border-gold" data-testid="groom-after-photo" />
                <figcaption className="text-[11px] text-gold text-center mt-1 uppercase tracking-wider font-bold">After ✨</figcaption>
              </figure>
            )}
          </div>
        )}
        {(r.coat || r.nails || r.ears) && (
          <div className="rounded-lg border border-[#EAEAEA] bg-cream/50 p-3 space-y-2">
            <Grade label="Coat" value={r.coat} />
            <Grade label="Nails" value={r.nails} />
            <Grade label="Ears" value={r.ears} />
          </div>
        )}
        {r.notes && (
          <p className="text-[13px] text-[#3a3a3a] italic border-l-2 border-gold pl-3" data-testid="groom-report-notes-view">"{r.notes}"</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
